import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { AuroraHeroFlower, AuroraHeroPhone } from '../../assets'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { XIcon } from '../../atoms/icons/XIcon'
import { Image } from '../../atoms/image/Image'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Subhead } from '../../atoms/subhead/Subhead'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { SubheadSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { Modal } from '../modal/Modal'

export interface AuroraLaunchAnnouncementModalProps {
  visible: boolean
  onClose: () => void
  onCheckItOut: () => void
}

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  position: 'relative',
  paddingTop: theme.spacing['16px'],
  gap: theme.spacing['24px'],
}))

const ImageCompositionContainer = styled.View({
  position: 'relative',
  width: 164,
  height: 157,
  alignSelf: 'center',
})

const FlowerImage = styled(Image)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 125,
  height: 125,
  borderRadius: 8,
})

const PhoneImage = styled(Image)({
  position: 'absolute',
  top: 32,
  left: 39,
  width: 125,
  height: 125,
  borderRadius: 8,
})

const CloseButtonContainer = styled.View({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
})

const TextContentSection = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['16px'],
  alignItems: 'center',
  alignSelf: 'stretch',
}))

const TitleContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
})

const BodyContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingHorizontal: theme.spacing['8px'],
  alignSelf: 'stretch',
}))

const ButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignSelf: 'stretch',
  marginTop: theme.spacing['8px'],
}))

export const AuroraLaunchAnnouncementModal: FunctionComponent<AuroraLaunchAnnouncementModalProps> = ({
  visible,
  onClose,
  onCheckItOut,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  const modalContents = (
    <ContentContainer testID={tID('AuroraLaunchAnnouncementModal')}>
      <CloseButtonContainer>
        <TertiaryIconButton
          testID={tID('AuroraLaunchAnnouncementModal-closeButton')}
          onPress={onClose}
          leftIcon={<XIcon fillColor={colors.iconDefault} />}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close',
            description: 'Accessibility label for close button on Aurora launch announcement modal',
          })}
        />
      </CloseButtonContainer>
      <ImageCompositionContainer>
        <FlowerImage
          source={AuroraHeroFlower}
          contentFit='cover'
          contentPosition='bottom'
          accessibilityIgnoresInvertColors
        />
        <PhoneImage source={AuroraHeroPhone} contentFit='cover' accessibilityIgnoresInvertColors />
      </ImageCompositionContainer>
      <TextContentSection>
        <TitleContainer>
          <Subhead
            size={SubheadSize.MEDIUM}
            text={
              <FormattedMessage
                defaultMessage='Lyra has a new look'
                description='Title of the Aurora launch announcement modal'
              />
            }
            textAlign='center'
            color={colors.textPrimary}
          />
        </TitleContainer>
        <BodyContainer>
          <BodyText
            text={
              <FormattedMessage
                defaultMessage="We've refreshed Lyra for faster access to the care you know and trust."
                description='Body text of the Aurora launch announcement modal'
              />
            }
            color={colors.textPrimary}
          />
        </BodyContainer>
      </TextContentSection>
      <ButtonContainer>
        <PrimaryButton
          testID={tID('AuroraLaunchAnnouncementModal-checkItOutButton')}
          onPress={onCheckItOut}
          text={
            <FormattedMessage
              defaultMessage='Check it out'
              description='CTA button text on Aurora launch announcement modal'
            />
          }
        />
      </ButtonContainer>
    </ContentContainer>
  )

  return (
    <Modal
      visible={visible}
      onRequestClose={onClose}
      onCloseEnd={onClose}
      modalContents={modalContents}
      width='544px'
      style={{ borderRadius: 24, backgroundColor: colors.backgroundModal }}
      showCloseIcon={false}
      disableBottomSheet
      disableFullScreenModal
      centered
      accessibilityLabel={formatMessage({
        defaultMessage: 'Aurora launch announcement',
        description: 'Accessibility label for Aurora launch announcement modal dialog',
      })}
    />
  )
}

export default AuroraLaunchAnnouncementModal
